import React, { useState, useContext, useEffect, useMemo } from "react";
import axios from "axios";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";
import ProjectPreOrderLayoutWrapper from "../../components/ProjectPreOrderLayoutWrapper";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaPlus, FaSearch, FaChevronDown, FaChevronUp } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const BASE_URL = import.meta?.env?.VITE_BASE_URL || "https://nissi-india.com";

const pad2 = (n) => String(n).padStart(2, "0");
const toInputDate = (d) =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

const getCurrentMonthRange = () => {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth(), 1);
  const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return { first, last };
};

// ✅ localStorage helper
const getLS = (key) => localStorage.getItem(key) || "";

const BoqListing = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const navigate = useNavigate();

  const { first, last } = getCurrentMonthRange();

  const [showFilters, setShowFilters] = useState(true);

  // Default current month
  const [fromDate, setFromDate] = useState(toInputDate(first));
  const [toDate, setToDate] = useState(toInputDate(last));

  // Dropdown selections
  const [work, setWork] = useState(""); // projectName
  const [boqCode, setBoqCode] = useState(""); // estimationCode

  // Dropdown lists
  const [projectList, setProjectList] = useState([]);
  const [estCodeList, setEstCodeList] = useState([]);

  // Listing + pagination
  // Listing + pagination
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const [selectedIds, setSelectedIds] = useState([]);

  // ✅ localStorage values
  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchId = getLS("branchId") || getLS("branchid");
  const branchType = getLS("branchType") || getLS("branchtype");
  const userId = getLS("userId") || getLS("loginuserid");

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branchtype": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  const handleAddNew = () => {
    navigate("/OrderManagement/EstimationDetails_1");
    toast.success("Add New BOQ clicked!");
  };

  const loadDropdowns = async () => {
    try {
      const [prjRes, codeRes] = await Promise.all([
        axios.get(`${BASE_URL}/api/boq/projects`, {
          headers: commonHeaders,
        }),
        axios.get(`${BASE_URL}/api/boq/estimationCodes`, {
          headers: commonHeaders,
        }),
      ]);

      setProjectList(prjRes.data?.list || []);
      setEstCodeList(codeRes.data?.list || []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load dropdowns");
    }
  };

  const loadList = async (pageNo = 1) => {
    try {
      setLoading(true);

      const params = {
        from: fromDate,
        to: toDate,
        projectName: work || "%",
        estCode: boqCode || "%",
        page: pageNo,
        pageSize,
      };

      const res = await axios.get(`${BASE_URL}/api/boq/list`, {
        params,
        headers: commonHeaders,
      });

      setRows(res.data?.rows || []);
      setPage(res.data?.page || pageNo);
      setTotalPages(res.data?.totalPages || 1);
      setTotal(res.data?.total || 0);
      setSelectedIds([]);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load list");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    toast.info("Searching BOQ records...");
    loadList(1);
  };

  const handleSelectRow = (estimationId, checked) => {
    setSelectedIds((prev) =>
      checked ? [...prev, estimationId] : prev.filter((id) => id !== estimationId)
    );
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.length === 0) {
      toast.error("Please select at least one record to delete");
      return;
    }

    const ok = window.confirm(
      `Are you sure you want to delete ${selectedIds.length} BOQ record(s)?`
    );
    if (!ok) return;

    try {
      const res = await axios.post(
        `${BASE_URL}/api/boq/delete`,
        { ids: selectedIds },
        { headers: commonHeaders }
      );

      if (!res.data?.success) {
        toast.error(res.data?.message || "Delete failed");
        return;
      }

      if (res.data.blockedIds?.length) {
        toast.warn(
          `Some records could not be deleted (finalized/referenced): ${res.data.blockedIds.join(", ")}`
        );
      }

      toast.success(res.data.message || "Deleted successfully");
      loadList(page);
    } catch (err) {
      console.error("Delete Error:", err);
      toast.error(err?.response?.data?.message || "Server error while deleting");
    }
  };

  useEffect(() => {
    loadDropdowns();
  }, [commonHeaders]);

  useEffect(() => {
    loadList(1);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <ProjectPreOrderLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">BOQ LISTING</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-4">
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                BOQ Listing
              </h1>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          <div
            className={`rounded-xl border mb-6 overflow-hidden transition-all duration-300 ${
              isDark ? "border-gray-700" : "border-gray-200"
            }`}
          >
            {showFilters && (
              <div
                className={`transition-all duration-300 ease-in-out ${
                  showFilters ? "max-h-none opacity-100" : "max-h-0 opacity-0 overflow-hidden"
                }`}
              >
                <div
                  className={`p-6 border-t ${
                    isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
                  }`}
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label
                        className={`block text-sm font-medium mb-2 ${
                          isDark ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        From Date
                      </label>
                      <input
                        type="date"
                        value={fromDate}
                        onChange={(e) => setFromDate(e.target.value)}
                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                          isDark
                            ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                            : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                        }`}
                      />
                    </div>

                    <div>
                      <label
                        className={`block text-sm font-medium mb-2 ${
                          isDark ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        To Date
                      </label>
                      <input
                        type="date"
                        value={toDate}
                        onChange={(e) => setToDate(e.target.value)}
                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                          isDark
                            ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                            : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                        }`}
                      />
                    </div>

                    <div>
                      <label
                        className={`block text-sm font-medium mb-2 ${
                          isDark ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Name of the Work
                      </label>
                      <select
                        value={work}
                        onChange={(e) => setWork(e.target.value)}
                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                          isDark
                            ? "bg-gray-700 border-gray-600 text-white"
                            : "bg-white border-gray-300 text-gray-900"
                        }`}
                      >
                        <option value="">Select</option>
                        {projectList.map((x) => (
                          <option key={x.estimationId} value={x.projectName}>
                            {x.projectName}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label
                        className={`block text-sm font-medium mb-2 ${
                          isDark ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        BOQ Code
                      </label>
                      <select
                        value={boqCode}
                        onChange={(e) => setBoqCode(e.target.value)}
                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                          isDark
                            ? "bg-gray-700 border-gray-600 text-white"
                            : "bg-white border-gray-300 text-gray-900"
                        }`}
                      >
                        <option value="">Select</option>
                        {estCodeList.map((x) => (
                          <option key={x.estimationId} value={x.estimationCode}>
                            {x.estimationCode}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                <div className="flex justify-center items-center mb-6 gap-6">
                  <button
                    onClick={handleSearch}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                      isDark
                        ? `${buttonClass} ${hoverClass} text-white`
                        : `${buttonClass} ${hoverClass} text-white`
                    }`}
                  >
                    <FaSearch />
                    Search
                  </button>

                  <button
                    onClick={handleAddNew}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                      isDark
                        ? `${buttonClass} ${hoverClass} text-white`
                        : `${buttonClass} ${hoverClass} text-white`
                    }`}
                  >
                    <FaPlus />
                    Add New
                  </button>
                </div>
              </div>
            )}
          </div>

          <div
            className={`rounded-xl shadow-sm border overflow-hidden ${
              isDark ? "border-gray-700" : "border-gray-200"
            }`}
          >
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${buttonClass} text-white`}>
                  <tr>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">
                      S.No
                    </th>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">
                      Date
                    </th>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">
                      BOQ Code
                    </th>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">
                      Name of the Work
                    </th>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">
                      Bidder Name
                    </th>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">
                      Status
                    </th>
                    <th className="px-4 py-4 text-center font-semibold">Select</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading && (
                    <tr>
                      <td
                        colSpan={7}
                        className={`text-center py-6 text-sm ${
                          isDark ? "text-gray-400 bg-gray-800" : "text-gray-500 bg-gray-50"
                        }`}
                      >
                        Loading...
                      </td>
                    </tr>
                  )}

                  {!loading &&
                    rows.map((r, idx) => (
                      <tr
                        key={r.estimationId}
                        className={
                          isDark
                            ? "bg-gray-800 hover:bg-gray-700"
                            : "bg-gray-50 hover:bg-gray-100"
                        }
                      >
                        <td className="px-4 py-3 text-center">
                          {(page - 1) * pageSize + idx + 1}
                        </td>

                        <td className="px-4 py-3 text-center">{r.date || "-"}</td>

                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() =>
                              navigate(`/OrderManagement/EstimationDetails_1/${r.preEstimationId}`)
                            }
                            className="text-blue-600 hover:underline font-semibold"
                          >
                            {r.estimationCode}
                          </button>
                        </td>

                        <td className="px-4 py-3 text-center">{r.projectName || "-"}</td>

                        <td className="px-4 py-3 text-center">{r.customerName || "-"}</td>

                        <td className="px-4 py-3 text-center">{r.status || "-"}</td>

                        <td className="px-4 py-3 text-center">
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(r.estimationId)}
                            onChange={(e) => handleSelectRow(r.estimationId, e.target.checked)}
                            className={`rounded focus:ring-2 focus:ring-blue-500 ${
                              isDark
                                ? "bg-gray-700 border-gray-600 text-blue-600"
                                : "bg-white border-gray-300 text-blue-600"
                            }`}
                          />
                        </td>
                      </tr>
                    ))}

                  {!loading && rows.length === 0 && (
                    <tr className={isDark ? "bg-gray-800" : "bg-gray-50"}>
                      <td
                        colSpan={7}
                        className={`text-center py-6 text-sm ${
                          isDark ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        No Records Found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex justify-between items-center mt-4">
            <div className="flex items-center gap-4">
              <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                Total: {total}
              </div>

              <button
                onClick={handleDeleteSelected}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200 ${
                  isDark
                    ? "bg-red-700 hover:bg-red-600 text-white"
                    : "bg-red-600 hover:bg-red-700 text-white"
                }`}
              >
                Delete Selected
              </button>
            </div>

            <div className="flex gap-2 items-center">
              <button
                disabled={page <= 1 || loading}
                onClick={() => loadList(page - 1)}
                className={`px-3 py-1 border rounded disabled:opacity-50 ${
                  isDark ? "border-gray-700 text-white" : "border-gray-300 text-gray-700"
                }`}
              >
                Prev
              </button>

              <span className={`px-2 py-1 text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                Page {page} / {totalPages}
              </span>

              <button
                disabled={page >= totalPages || loading}
                onClick={() => loadList(page + 1)}
                className={`px-3 py-1 border rounded disabled:opacity-50 ${
                  isDark ? "border-gray-700 text-white" : "border-gray-300 text-gray-700"
                }`}
              >
                Next
              </button>
            </div>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </ProjectPreOrderLayoutWrapper>
  );
};

export default BoqListing;